import React, { useState, useEffect, useCallback } from "react";
import { api, formatApiError, formatDate } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import ConfirmDialog from "../components/ConfirmDialog";
import { useConfirm } from "../components/useConfirm";
import { toast } from "sonner";
import { N2OfferSettings } from "../components/admin/N2OfferSettings";

export default function AdminSettingsPage() {
  const [settings, setSettings] = useState({ env: [], editable: {} });
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [normItems, setNormItems] = useState([]);
  const [loadingNorm, setLoadingNorm] = useState(true);
  const [normFilter, setNormFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [adminEmail, setAdminEmail] = useState("");
  const [savingAdminEmail, setSavingAdminEmail] = useState(false);

  // Iter24.d: confirm dialog reutilizable
  const { confirm, dialogProps: confirmDialogProps } = useConfirm();

  const loadSettings = useCallback(async () => {
    setLoadingSettings(true);
    try {
      const { data } = await api.get("/admin/settings");
      setSettings(data);
      setAdminEmail(data.editable?.PAYMENT_N1_ADMIN_CONTACT_EMAIL?.value || "");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Error cargando settings"); }
    finally { setLoadingSettings(false); }
  }, []);

  const loadNorm = useCallback(async () => {
    setLoadingNorm(true);
    try {
      const params = new URLSearchParams();
      if (normFilter.trim()) params.set("q", normFilter.trim());
      const { data } = await api.get(`/admin/normativa${params.toString() ? `?${params}` : ""}`);
      setNormItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Error cargando normativa"); }
    finally { setLoadingNorm(false); }
  }, [normFilter]);

  useEffect(() => { loadSettings(); }, [loadSettings]);
  useEffect(() => { loadNorm(); }, [loadNorm]);

  const saveAdminEmail = async () => {
    if (!adminEmail || adminEmail.length < 3) return toast.error("Email inválido");
    setSavingAdminEmail(true);
    try {
      await api.post("/admin/settings", { key: "PAYMENT_N1_ADMIN_CONTACT_EMAIL", value: adminEmail });
      toast.success("Email de contacto actualizado");
      await loadSettings();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Error guardando"); }
    finally { setSavingAdminEmail(false); }
  };

  const deleteNorm = async (item) => {
    const ok = await confirm({
      title: "Eliminar norma",
      description: <>¿Eliminar <strong>"{item.alias}"</strong>? Esta acción es auditada.</>,
      confirmLabel: "Eliminar",
      destructive: true,
      testIdPrefix: "norm-delete-confirm",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/normativa/${item.id}`);
      toast.success("Norma eliminada");
      await loadNorm();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Error eliminando"); }
  };

  return (
    <div data-testid="admin-settings-page">
      <p className="text-xs uppercase tracking-[0.22em] text-primary font-medium mb-1">Operación</p>
      <h1 className="font-serif text-3xl text-gray-900 mb-1">Configuración</h1>
      <p className="text-xs text-gray-500 mb-6">Variables de entorno y configuración mutable del sistema.</p>

      {/* Editable settings */}
      <section className="mb-10" data-testid="settings-editable">
        <h2 className="font-serif text-xl text-gray-900 mb-3">Editable</h2>
        <div className="bg-white border border-gray-200 p-5">
          <label className="block text-xs uppercase tracking-widest text-gray-500 mb-1">PAYMENT_N1_ADMIN_CONTACT_EMAIL</label>
          <p className="text-[11px] text-gray-500 mb-2">Email donde llegan recordatorios de Kit Documental (≥3 días sin contacto). Sobreescribe variable de entorno.</p>
          <div className="flex gap-2 items-center">
            <Input value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} className="rounded-sm h-9 max-w-md" data-testid="settings-admin-email-input" />
            <Button className="rounded-sm h-9" disabled={savingAdminEmail} onClick={saveAdminEmail} data-testid="settings-admin-email-save">{savingAdminEmail ? "Guardando…" : "Guardar"}</Button>
          </div>
          <p className="text-[11px] text-gray-400 mt-2">
            Origen actual: {settings.editable?.PAYMENT_N1_ADMIN_CONTACT_EMAIL?.source || "—"}
            {settings.editable?.PAYMENT_N1_ADMIN_CONTACT_EMAIL?.updated_at && ` · actualizado ${formatDate(settings.editable.PAYMENT_N1_ADMIN_CONTACT_EMAIL.updated_at)}`}
          </p>
        </div>
      </section>

      {/* P1: Oferta comercial N2 */}
      <N2OfferSettings />

      {/* Fase 11.4: MercadoPago Checkout Pro */}
      <MercadoPagoSettings settings={settings} reload={loadSettings} />

      {/* Sprint 1: Pagos manuales configuration */}
      <ManualPaymentSettings settings={settings} reload={loadSettings} />

      {/* Fase 12: Email (Resend) configuration */}
      <EmailSettings settings={settings} reload={loadSettings} />

      {/* Iter18: Operaciones (cleanup + quick replies) */}
      <OperationsSettings settings={settings} reload={loadSettings} />

      {/* Read-only env */}
      <section className="mb-10" data-testid="settings-env">
        <h2 className="font-serif text-xl text-gray-900 mb-3">Variables de entorno (solo lectura)</h2>
        <div className="border border-gray-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-[11px] uppercase tracking-widest text-gray-500 bg-gray-50 border-b">
              <th className="px-3 py-3 text-left">Variable</th>
              <th className="px-3 py-3 text-left">Valor</th>
              <th className="px-3 py-3 text-left">Estado</th>
            </tr></thead>
            <tbody>
              {loadingSettings && <tr><td colSpan={3} className="px-4 py-6 text-center text-gray-500">Cargando…</td></tr>}
              {!loadingSettings && settings.env.map((v) => (
                <tr key={v.key} className="border-b border-gray-100" data-testid={`env-row-${v.key}`}>
                  <td className="px-3 py-2 font-mono text-[11px] text-gray-800">{v.key}</td>
                  <td className="px-3 py-2 font-mono text-[11px] text-gray-600 truncate max-w-[460px]">{v.value || <span className="text-gray-400">— vacío —</span>}</td>
                  <td className="px-3 py-2 text-[11px]">
                    {v.is_set ? <span className="text-emerald-700">configurada</span> : <span className="text-amber-700">no configurada</span>}
                    {v.redacted && <span className="ml-2 text-gray-400">(redactada)</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Normativa whitelist CRUD */}
      <section data-testid="settings-normativa">
        <div className="flex items-end justify-between mb-3 flex-wrap gap-3">
          <div>
            <h2 className="font-serif text-xl text-gray-900">Lista blanca normativa</h2>
            <p className="text-xs text-gray-500">{normItems.length} normas activas en la colección Mongo.</p>
          </div>
          <div className="flex gap-2">
            <Input value={normFilter} onChange={(e) => setNormFilter(e.target.value)} placeholder="Buscar alias / tema…" className="rounded-sm h-9 w-64" data-testid="norm-filter" />
            <Button className="rounded-sm h-9" onClick={() => setEditing({})} data-testid="norm-new-btn">+ Nueva norma</Button>
          </div>
        </div>
        <div className="border border-gray-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-[11px] uppercase tracking-widest text-gray-500 bg-gray-50 border-b">
              <th className="px-3 py-3 text-left">Tipo</th>
              <th className="px-3 py-3 text-left">Número</th>
              <th className="px-3 py-3 text-left">Año</th>
              <th className="px-3 py-3 text-left">Alias</th>
              <th className="px-3 py-3 text-left">Tema</th>
              <th className="px-3 py-3 text-left">Vigente</th>
              <th className="px-3 py-3 text-right">Acciones</th>
            </tr></thead>
            <tbody>
              {loadingNorm && <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-500">Cargando…</td></tr>}
              {!loadingNorm && normItems.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-500" data-testid="norm-empty">Sin normas.</td></tr>
              )}
              {!loadingNorm && normItems.map((n) => (
                <tr key={n.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`norm-row-${n.id}`}>
                  <td className="px-3 py-2 text-xs">{n.tipo}</td>
                  <td className="px-3 py-2 text-xs">{n.numero}</td>
                  <td className="px-3 py-2 text-xs">{n.anio}</td>
                  <td className="px-3 py-2 text-xs max-w-[260px] truncate">{n.alias}</td>
                  <td className="px-3 py-2 text-xs max-w-[240px] truncate text-gray-600">{n.tema}</td>
                  <td className="px-3 py-2 text-xs">{n.vigente ? "Sí" : "No"}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" className="rounded-sm h-7 text-[11px] mr-1" onClick={() => setEditing(n)} data-testid={`norm-edit-${n.id}`}>Editar</Button>
                    <Button size="sm" variant="outline" className="rounded-sm h-7 text-[11px] border-rose-300 text-rose-700" onClick={() => deleteNorm(n)} data-testid={`norm-delete-${n.id}`}>Eliminar</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <NormDialog item={editing} onClose={() => setEditing(null)} onSaved={loadNorm} />

      {/* Iter24.d — Confirm dialog reutilizable */}
      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
}

function NormDialog({ item, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      setForm({
        tipo: item.tipo || "ley",
        numero: item.numero || "",
        anio: item.anio || new Date().getFullYear(),
        alias: item.alias || "",
        tema: item.tema || "",
        categoria: item.categoria || "Estatuto General",
        vigente: item.vigente !== false,
        explainer: item.explainer || "",
      });
    }
  }, [item]);

  const open = !!item;
  const isEdit = !!item?.id;
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form, anio: parseInt(form.anio, 10) };
      if (isEdit) await api.put(`/admin/normativa/${item.id}`, body);
      else await api.post("/admin/normativa", body);
      toast.success(isEdit ? "Norma actualizada" : "Norma creada");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error guardando norma");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none border border-gray-200 max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{isEdit ? "Editar norma" : "Nueva norma"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <Field label="Tipo" v={form.tipo} onChange={(v) => update("tipo", v)} testid="norm-tipo" />
          <Field label="Número" v={form.numero} onChange={(v) => update("numero", v)} testid="norm-numero" />
          <Field label="Año" type="number" v={form.anio} onChange={(v) => update("anio", v)} testid="norm-anio" />
          <Field label="Categoría" v={form.categoria} onChange={(v) => update("categoria", v)} testid="norm-categoria" />
          <Field label="Alias" v={form.alias} onChange={(v) => update("alias", v)} className="col-span-2" testid="norm-alias" />
          <Field label="Tema" v={form.tema} onChange={(v) => update("tema", v)} className="col-span-2" testid="norm-tema" />
          <div className="col-span-2">
            <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Explicación</label>
            <Textarea value={form.explainer || ""} onChange={(e) => update("explainer", e.target.value)} rows={4} className="rounded-sm" data-testid="norm-explainer" />
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-700 col-span-2">
            <input type="checkbox" checked={form.vigente !== false} onChange={(e) => update("vigente", e.target.checked)} data-testid="norm-vigente" />
            Vigente
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose}>Cancelar</Button>
          <Button className="rounded-sm" disabled={saving} onClick={save} data-testid="norm-save">{saving ? "Guardando…" : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, v, onChange, type = "text", testid, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">{label}</label>
      <Input type={type} value={v ?? ""} onChange={(e) => onChange(e.target.value)} className="rounded-sm h-9 text-xs" data-testid={testid} />
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Fase 11.4 — MercadoPago Checkout Pro settings
// ─────────────────────────────────────────────────────────────────────────

function MercadoPagoSettings({ settings, reload }) {
  const ed = settings?.editable || {};
  const enabled = Boolean(ed.mercadopago_enabled?.raw_value);
  const mode = ed.mercadopago_mode?.raw_value || "sandbox";
  const tokenSet = Boolean(ed.mercadopago_access_token?.is_set);
  const pubSet = Boolean(ed.mercadopago_public_key?.is_set);
  const secretSet = Boolean(ed.mercadopago_webhook_secret?.is_set);

  const [savingFlag, setSavingFlag] = React.useState(false);
  const [editToken, setEditToken] = React.useState("");
  const [editPub, setEditPub] = React.useState("");
  const [editSecret, setEditSecret] = React.useState("");
  const [showSecrets, setShowSecrets] = React.useState(false);
  const [savingCreds, setSavingCreds] = React.useState(false);

  // Iter24.d: confirm dialog reutilizable
  const { confirm: mpConfirm, dialogProps: mpConfirmDialogProps } = useConfirm();

  const setSetting = async (key, value) => {
    try {
      await api.post("/admin/settings", { key, value: String(value) });
    } catch (e) {
      throw e;
    }
  };

  const toggleEnabled = async (next) => {
    // Confirmación destructiva si se va a deshabilitar
    if (!next) {
      const ok = await mpConfirm({
        title: "Deshabilitar MercadoPago",
        description: "¿Deshabilitar MercadoPago? Los usuarios NO podrán pagar con tarjeta/PSE/Efecty hasta que se reactive. Esto afecta inmediatamente el flujo de pago en producción.",
        confirmLabel: "Sí, deshabilitar",
        destructive: true,
        testIdPrefix: "mp-disable-confirm",
      });
      if (!ok) return;
    }
    setSavingFlag(true);
    try {
      await setSetting("mercadopago_enabled", next ? "true" : "false");
      toast.success(next ? "MercadoPago habilitado" : "MercadoPago deshabilitado");
      reload();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error guardando flag");
    } finally {
      setSavingFlag(false);
    }
  };

  const changeMode = async (newMode) => {
    setSavingFlag(true);
    try {
      await setSetting("mercadopago_mode", newMode);
      toast.success(`Modo cambiado a ${newMode}`);
      reload();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error guardando modo");
    } finally {
      setSavingFlag(false);
    }
  };

  const saveCreds = async () => {
    setSavingCreds(true);
    try {
      const tasks = [];
      if (editToken && editToken.length > 8) tasks.push(setSetting("mercadopago_access_token", editToken));
      if (editPub && editPub.length > 8) tasks.push(setSetting("mercadopago_public_key", editPub));
      if (editSecret && editSecret.length > 8) tasks.push(setSetting("mercadopago_webhook_secret", editSecret));
      if (tasks.length === 0) {
        toast.info("No hay credenciales nuevas para guardar");
      } else {
        await Promise.all(tasks);
        toast.success("Credenciales MercadoPago actualizadas");
        setEditToken("");
        setEditPub("");
        setEditSecret("");
        reload();
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error guardando credenciales");
    } finally {
      setSavingCreds(false);
    }
  };

  return (
    <section className="mb-10" data-testid="mp-settings">
      <h2 className="font-serif text-xl text-gray-900 mb-1">Pasarela MercadoPago (Checkout Pro)</h2>
      <p className="text-xs text-gray-500 mb-4">
        Pago automático con tarjeta para Diagnóstico (COP 350.000) y Kit Documental (COP 1.200.000).
        Mientras esté <strong>deshabilitado</strong>, el flujo manual sigue funcionando.
      </p>

      <div className="bg-white border border-gray-200 p-5">
        {/* Toggle enabled */}
        <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100">
          <div>
            <p className="text-sm font-medium text-gray-900">Estado de la integración</p>
            <p className="text-xs text-gray-500">
              {enabled
                ? "✅ Habilitado. Los endpoints `init-mercadopago` están activos."
                : "⛔ Deshabilitado. Solo flujo de pago manual."}
            </p>
          </div>
          <button
            onClick={() => toggleEnabled(!enabled)}
            disabled={savingFlag}
            className={`px-4 py-2 rounded-sm text-sm border ${enabled
              ? "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700"
              : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`}
            data-testid="mp-toggle-enabled"
          >
            {enabled ? "Habilitado" : "Deshabilitado"}
          </button>
        </div>

        {/* Mode */}
        <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100">
          <div>
            <p className="text-sm font-medium text-gray-900">Modo de operación</p>
            <p className="text-xs text-gray-500">
              Sandbox: usa credenciales de prueba. Production: cobra dinero real.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant={mode === "sandbox" ? "default" : "outline"}
              size="sm"
              onClick={() => changeMode("sandbox")}
              disabled={savingFlag || mode === "sandbox"}
              className="rounded-sm h-8 text-xs"
              data-testid="mp-mode-sandbox"
            >
              Sandbox
            </Button>
            <Button
              variant={mode === "production" ? "default" : "outline"}
              size="sm"
              onClick={() => changeMode("production")}
              disabled={savingFlag || mode === "production"}
              className="rounded-sm h-8 text-xs"
              data-testid="mp-mode-production"
            >
              Producción
            </Button>
          </div>
        </div>

        {/* Status badges credentials */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <CredStatus label="ACCESS_TOKEN" set={tokenSet} source={ed.mercadopago_access_token?.source} />
          <CredStatus label="PUBLIC_KEY" set={pubSet} source={ed.mercadopago_public_key?.source} />
          <CredStatus label="WEBHOOK_SECRET" set={secretSet} source={ed.mercadopago_webhook_secret?.source} />
        </div>

        {/* Edit credentials */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-widest text-gray-500">Actualizar credenciales</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSecrets((s) => !s)}
              className="rounded-sm h-7 text-[11px]"
              data-testid="mp-toggle-show-secrets"
            >
              {showSecrets ? "Ocultar campos" : "Editar"}
            </Button>
          </div>
          {showSecrets && (
            <div className="space-y-3 mt-3">
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">
                  Access Token (APP_USR-...)
                </label>
                <Input
                  type="password"
                  value={editToken}
                  onChange={(e) => setEditToken(e.target.value)}
                  placeholder="Dejar vacío para mantener el actual"
                  className="rounded-sm h-9 text-xs font-mono"
                  data-testid="mp-edit-token"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">
                  Public Key
                </label>
                <Input
                  type="text"
                  value={editPub}
                  onChange={(e) => setEditPub(e.target.value)}
                  placeholder="Dejar vacío para mantener el actual"
                  className="rounded-sm h-9 text-xs font-mono"
                  data-testid="mp-edit-pub"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">
                  Webhook Secret (HMAC validation)
                </label>
                <Input
                  type="password"
                  value={editSecret}
                  onChange={(e) => setEditSecret(e.target.value)}
                  placeholder="Dejar vacío para mantener el actual"
                  className="rounded-sm h-9 text-xs font-mono"
                  data-testid="mp-edit-secret"
                />
                <p className="text-[10px] text-gray-400 mt-1">
                  Sin secret, los webhooks no se validan (no recomendado en producción).
                </p>
              </div>
              <Button
                onClick={saveCreds}
                disabled={savingCreds}
                className="rounded-sm h-9"
                data-testid="mp-save-creds"
              >
                {savingCreds ? "Guardando…" : "Guardar credenciales"}
              </Button>
            </div>
          )}
        </div>

        {/* Webhook URL info */}
        <div className="mt-4 pt-4 border-t border-gray-100 bg-gray-50 -mx-5 -mb-5 px-5 py-4 rounded-b-sm">
          <p className="text-[11px] uppercase tracking-widest text-gray-500 mb-1">Webhook URL</p>
          <code className="text-xs text-gray-700 font-mono break-all">
            {(window.location.origin)}/api/payments/mercadopago/webhook
          </code>
          <p className="text-[11px] text-gray-500 mt-2">
            Configura esta URL en el panel de MercadoPago → Tu integración → Webhooks (tópico:
            <strong> payments</strong>). MP enviará notificaciones aquí cuando un pago cambie de
            estado.
          </p>
        </div>
      </div>
      <ConfirmDialog {...mpConfirmDialogProps} />
    </section>
  );
}

function CredStatus({ label, set, source }) {
  return (
    <div className="border border-gray-200 bg-gray-50 p-3 rounded-sm">
      <p className="text-[10px] uppercase tracking-widest text-gray-500">{label}</p>
      <p className={`text-xs font-medium ${set ? "text-emerald-700" : "text-amber-700"}`}>
        {set ? "✓ configurada" : "⚠ no configurada"}
      </p>
      {source && <p className="text-[10px] text-gray-400 mt-1">origen: {source}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Sprint 1 — Manual Payment Settings
// ─────────────────────────────────────────────────────────────────────────

function ManualPaymentSettings({ settings, reload }) {
  const ed = settings?.editable || {};
  
  const [editBankInfo, setEditBankInfo] = React.useState("");
  const [editQrUrl, setEditQrUrl] = React.useState("");
  const [editExtLink, setEditExtLink] = React.useState("");
  const [editInstructions, setEditInstructions] = React.useState("");
  const [saving, setSaving] = React.useState({ bank: false, qr: false, ext: false, instr: false });

  const setSetting = async (key, value) => {
    try {
      await api.post("/admin/settings", { key, value: String(value) });
    } catch (e) {
      throw e;
    }
  };

  const saveBankInfo = async () => {
    if (!editBankInfo.trim()) {
      toast.error("Ingresa los datos bancarios");
      return;
    }
    // Validate JSON if it starts with { or [
    if (editBankInfo.trim().startsWith("{") || editBankInfo.trim().startsWith("[")) {
      try {
        JSON.parse(editBankInfo);
      } catch {
        toast.error("JSON inválido. Verifica la sintaxis.");
        return;
      }
    }
    setSaving((s) => ({ ...s, bank: true }));
    try {
      await setSetting("manual_payment_bank_info", editBankInfo.trim());
      toast.success("Datos bancarios actualizados");
      setEditBankInfo("");
      reload();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error guardando datos bancarios");
    } finally {
      setSaving((s) => ({ ...s, bank: false }));
    }
  };

  const saveQrUrl = async () => {
    if (!editQrUrl.trim()) {
      toast.error("Ingresa la URL del QR Bre-B");
      return;
    }
    setSaving((s) => ({ ...s, qr: true }));
    try {
      await setSetting("manual_payment_qr_url", editQrUrl.trim());
      toast.success("URL QR Bre-B actualizada");
      setEditQrUrl("");
      reload();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error guardando QR URL");
    } finally {
      setSaving((s) => ({ ...s, qr: false }));
    }
  };

  const saveExtLink = async () => {
    if (!editExtLink.trim()) {
      toast.error("Ingresa el link de la pasarela externa");
      return;
    }
    setSaving((s) => ({ ...s, ext: true }));
    try {
      await setSetting("manual_payment_external_link", editExtLink.trim());
      toast.success("Link pasarela externa actualizado");
      setEditExtLink("");
      reload();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error guardando link externo");
    } finally {
      setSaving((s) => ({ ...s, ext: false }));
    }
  };

  const saveInstructions = async () => {
    if (!editInstructions.trim()) {
      toast.error("Ingresa las instrucciones adicionales");
      return;
    }
    setSaving((s) => ({ ...s, instr: true }));
    try {
      await setSetting("manual_payment_instructions", editInstructions.trim());
      toast.success("Instrucciones adicionales actualizadas");
      setEditInstructions("");
      reload();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error guardando instrucciones");
    } finally {
      setSaving((s) => ({ ...s, instr: false }));
    }
  };

  const bankInfoCurrent = ed.manual_payment_bank_info?.value || "";
  const qrUrlCurrent = ed.manual_payment_qr_url?.value || "";
  const extLinkCurrent = ed.manual_payment_external_link?.value || "";
  const instrCurrent = ed.manual_payment_instructions?.value || "";

  const bankInfoSource = ed.manual_payment_bank_info?.source || "default";
  const qrUrlSource = ed.manual_payment_qr_url?.source || "default";
  const extLinkSource = ed.manual_payment_external_link?.source || "default";
  const instrSource = ed.manual_payment_instructions?.source || "default";

  return (
    <section className="mb-10" data-testid="manual-payment-settings">
      <h2 className="font-serif text-xl text-gray-900 mb-1">Pagos manuales</h2>
      <p className="text-xs text-gray-500 mb-4">
        Configura los métodos de pago manual que se mostrarán a los usuarios cuando MercadoPago no esté disponible o elijan otra opción.
      </p>

      <div className="bg-white border border-gray-200 p-5 space-y-6">
        {/* 1. Datos bancarios */}
        <div className="pb-6 border-b border-gray-100">
          <label className="block text-xs uppercase tracking-widest text-gray-500 mb-1">Datos bancarios (JSON o texto)</label>
          <p className="text-[11px] text-gray-500 mb-2">
            Puedes usar JSON: <code className="font-mono text-[10px] bg-gray-100 px-1">{"{"}"banco": "Bancolombia", "tipo": "Ahorros", "numero": "123-456789-01", "titular": "JustSapiens SAS", "nit": "900..."{"}"}</code> o texto plano.
          </p>
          {bankInfoCurrent && (
            <div className="mb-2 p-3 bg-gray-50 border border-gray-200 rounded-sm">
              <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Valor actual</p>
              <pre className="text-[11px] text-gray-700 font-mono whitespace-pre-wrap break-words">{bankInfoCurrent}</pre>
              <p className="text-[10px] text-gray-400 mt-1">Origen: {bankInfoSource}</p>
            </div>
          )}
          <Textarea
            value={editBankInfo}
            onChange={(e) => setEditBankInfo(e.target.value)}
            rows={4}
            placeholder='Ej: {"banco": "Bancolombia", "tipo": "Ahorros", "numero": "123-456789-01", "titular": "JustSapiens SAS", "nit": "900..."}'
            className="rounded-sm text-xs font-mono mb-2"
            data-testid="manual-bank-info-input"
          />
          <Button
            onClick={saveBankInfo}
            disabled={saving.bank}
            className="rounded-sm h-9"
            data-testid="manual-bank-info-save"
          >
            {saving.bank ? "Guardando…" : "Guardar datos bancarios"}
          </Button>
        </div>

        {/* 2. QR Bre-B URL */}
        <div className="pb-6 border-b border-gray-100">
          <label className="block text-xs uppercase tracking-widest text-gray-500 mb-1">QR Bre-B URL</label>
          <p className="text-[11px] text-gray-500 mb-2">Link directo a la imagen del QR Bre-B.</p>
          {qrUrlCurrent && (
            <div className="mb-2 p-3 bg-gray-50 border border-gray-200 rounded-sm">
              <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Valor actual</p>
              <p className="text-[11px] text-gray-700 break-all">{qrUrlCurrent}</p>
              <p className="text-[10px] text-gray-400 mt-1">Origen: {qrUrlSource}</p>
            </div>
          )}
          <Input
            type="url"
            value={editQrUrl}
            onChange={(e) => setEditQrUrl(e.target.value)}
            placeholder="https://ejemplo.com/qr-bre-b.png"
            className="rounded-sm h-9 text-xs mb-2"
            data-testid="manual-qr-url-input"
          />
          <Button
            onClick={saveQrUrl}
            disabled={saving.qr}
            className="rounded-sm h-9"
            data-testid="manual-qr-url-save"
          >
            {saving.qr ? "Guardando…" : "Guardar QR URL"}
          </Button>
        </div>

        {/* 3. Link pasarela externa */}
        <div className="pb-6 border-b border-gray-100">
          <label className="block text-xs uppercase tracking-widest text-gray-500 mb-1">Link pasarela externa</label>
          <p className="text-[11px] text-gray-500 mb-2">Link a epayco, PayU u otra pasarela de pago.</p>
          {extLinkCurrent && (
            <div className="mb-2 p-3 bg-gray-50 border border-gray-200 rounded-sm">
              <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Valor actual</p>
              <p className="text-[11px] text-gray-700 break-all">{extLinkCurrent}</p>
              <p className="text-[10px] text-gray-400 mt-1">Origen: {extLinkSource}</p>
            </div>
          )}
          <Input
            type="url"
            value={editExtLink}
            onChange={(e) => setEditExtLink(e.target.value)}
            placeholder="https://checkout.epayco.co/..."
            className="rounded-sm h-9 text-xs mb-2"
            data-testid="manual-ext-link-input"
          />
          <Button
            onClick={saveExtLink}
            disabled={saving.ext}
            className="rounded-sm h-9"
            data-testid="manual-ext-link-save"
          >
            {saving.ext ? "Guardando…" : "Guardar link externo"}
          </Button>
        </div>

        {/* 4. Instrucciones adicionales */}
        <div>
          <label className="block text-xs uppercase tracking-widest text-gray-500 mb-1">Instrucciones adicionales</label>
          <p className="text-[11px] text-gray-500 mb-2">Texto libre con indicaciones extra para el usuario.</p>
          {instrCurrent && (
            <div className="mb-2 p-3 bg-gray-50 border border-gray-200 rounded-sm">
              <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Valor actual</p>
              <p className="text-[11px] text-gray-700 whitespace-pre-wrap">{instrCurrent}</p>
              <p className="text-[10px] text-gray-400 mt-1">Origen: {instrSource}</p>
            </div>
          )}
          <Textarea
            value={editInstructions}
            onChange={(e) => setEditInstructions(e.target.value)}
            rows={3}
            placeholder="Ej: Envía comprobante a contacto@procure.justsapiens.com con la referencia"
            className="rounded-sm text-xs mb-2"
            data-testid="manual-instr-input"
          />
          <Button
            onClick={saveInstructions}
            disabled={saving.instr}
            className="rounded-sm h-9"
            data-testid="manual-instr-save"
          >
            {saving.instr ? "Guardando…" : "Guardar instrucciones"}
          </Button>
        </div>
      </div>
    </section>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Fase 12 — Email (Resend) settings + Bre-B key con QR preview
// ─────────────────────────────────────────────────────────────────────────


function EmailSettings({ settings, reload }) {
  const ed = settings?.editable || {};
  const apiKeyMeta = ed.resend_api_key || {};
  const fromMeta = ed.email_from || {};
  const webhookSecretMeta = ed.resend_webhook_secret || {};
  const breBMeta = ed.manual_payment_bre_b_key || {};
  const fbuMeta = ed.frontend_base_url || {};

  const [apiKey, setApiKey] = React.useState("");
  const [fromAddr, setFromAddr] = React.useState(fromMeta.raw_value || "");
  const [webhookSecret, setWebhookSecret] = React.useState("");
  const [breBKey, setBreBKey] = React.useState(breBMeta.raw_value || "");
  const [fbu, setFbu] = React.useState(fbuMeta.raw_value || fbuMeta.value || "");
  const [saving, setSaving] = React.useState({ apiKey: false, from: false, webhookSecret: false, breB: false, qrImg: false, fbu: false });

  React.useEffect(() => {
    setFromAddr(fromMeta.raw_value || fromMeta.value || "");
    setBreBKey(breBMeta.raw_value || breBMeta.value || "");
    setFbu(fbuMeta.raw_value || fbuMeta.value || "");
  }, [fromMeta.raw_value, fromMeta.value, breBMeta.raw_value, breBMeta.value, fbuMeta.raw_value, fbuMeta.value]);

  const save = async (key, value, label) => {
    if (!value || !value.trim()) {
      toast.error(`${label}: el valor no puede estar vacío`);
      return;
    }
    setSaving((s) => ({ ...s, [label]: true }));
    try {
      await api.post("/admin/settings", { key, value: String(value) });
      toast.success(`${label} guardado`);
      reload();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error guardando");
    } finally {
      setSaving((s) => ({ ...s, [label]: false }));
    }
  };

  // (Vista previa removida — ahora el admin sube directamente la imagen QR)

  return (
    <section className="mb-10" data-testid="email-settings">
      <h2 className="font-serif text-xl text-gray-900 mb-3">Email transaccional (Resend)</h2>
      <p className="text-sm text-gray-600 mb-4">
        Configura el remitente y la API key de Resend. Los cambios aplican sin redeploy.
      </p>

      <div className="border border-gray-200 bg-white p-5 space-y-5">
        {/* API key */}
        <div>
          <label className="block text-[11px] uppercase tracking-widest text-gray-600 mb-1">
            Resend API key
          </label>
          <div className="flex items-center gap-2 mb-2 text-xs">
            <span className="text-gray-500">Estado:</span>
            <span className={`px-2 py-0.5 border rounded-sm ${apiKeyMeta.is_set ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-gray-50 border-gray-300 text-gray-600"}`}>
              {apiKeyMeta.is_set ? `Configurada (${apiKeyMeta.value})` : "Sin configurar"}
            </span>
            {apiKeyMeta.source && (
              <span className="text-[10px] uppercase tracking-widest text-gray-500">source: {apiKeyMeta.source}</span>
            )}
          </div>
          <div className="flex gap-2">
            <Input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="re_..."
              className="rounded-sm h-9 text-xs font-mono"
              data-testid="resend-api-key-input"
            />
            <Button
              onClick={() => save("resend_api_key", apiKey, "apiKey")}
              disabled={saving.apiKey || !apiKey.trim()}
              className="rounded-sm h-9 text-xs"
              data-testid="resend-api-key-save"
            >
              {saving.apiKey ? "Guardando…" : "Guardar"}
            </Button>
          </div>
          <p className="text-[10px] text-gray-500 mt-1">Obtén tu API key desde https://resend.com/api-keys</p>
        </div>

        {/* From address */}
        <div className="pt-3 border-t border-gray-100">
          <label className="block text-[11px] uppercase tracking-widest text-gray-600 mb-1">
            Email remitente (EMAIL_FROM)
          </label>
          <div className="flex gap-2">
            <Input
              value={fromAddr}
              onChange={(e) => setFromAddr(e.target.value)}
              placeholder="JustSapiens Procure <noreply@procure.justsapiens.com>"
              className="rounded-sm h-9 text-xs"
              data-testid="email-from-input"
            />
            <Button
              onClick={() => save("email_from", fromAddr, "from")}
              disabled={saving.from || !fromAddr.trim()}
              className="rounded-sm h-9 text-xs"
              data-testid="email-from-save"
            >
              {saving.from ? "Guardando…" : "Guardar"}
            </Button>
          </div>
          <p className="text-[10px] text-gray-500 mt-1">El dominio debe estar verificado en Resend. Formato: "Nombre &lt;email@dominio.com&gt;"</p>
        </div>

        {/* Webhook signing secret (inbound + events) */}
        <div className="pt-3 border-t border-gray-100">
          <label className="block text-[11px] uppercase tracking-widest text-gray-600 mb-1">
            Resend webhook signing secret
          </label>
          <div className="flex items-center gap-2 mb-2 text-xs">
            <span className="text-gray-500">Estado:</span>
            <span className={`px-2 py-0.5 border rounded-sm ${webhookSecretMeta.is_set ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-gray-50 border-gray-300 text-gray-600"}`}>
              {webhookSecretMeta.is_set ? `Configurado (${webhookSecretMeta.value})` : "Sin configurar"}
            </span>
            {webhookSecretMeta.source && (
              <span className="text-[10px] uppercase tracking-widest text-gray-500">source: {webhookSecretMeta.source}</span>
            )}
          </div>
          <div className="flex gap-2">
            <Input
              type="password"
              value={webhookSecret}
              onChange={(e) => setWebhookSecret(e.target.value)}
              placeholder="whsec_..."
              className="rounded-sm h-9 text-xs font-mono"
              data-testid="resend-webhook-secret-input"
            />
            <Button
              onClick={() => save("resend_webhook_secret", webhookSecret, "webhookSecret")}
              disabled={saving.webhookSecret || !webhookSecret.trim()}
              className="rounded-sm h-9 text-xs"
              data-testid="resend-webhook-secret-save"
            >
              {saving.webhookSecret ? "Guardando…" : "Guardar"}
            </Button>
          </div>
          <p className="text-[10px] text-gray-500 mt-1">
            Firma de los webhooks Resend (Inbound + eventos). Obténlo en{" "}
            <a href="https://resend.com/webhooks" target="_blank" rel="noreferrer" className="text-brand-burgundy hover:underline">resend.com/webhooks</a>{" "}
            al crear el endpoint. Formato: <code>whsec_...</code>
          </p>
          <p className="text-[10px] text-gray-500 mt-1">
            URLs de webhook a registrar:
            <br /><code className="text-gray-700">{`${window.location.origin}/api/webhooks/resend/inbound`}</code>
            <br /><code className="text-gray-700">{`${window.location.origin}/api/webhooks/resend/events`}</code>
          </p>
        </div>

        {/* FRONTEND_BASE_URL — base de los enlaces de los emails (verify, magic links, etc.) */}
        <div className="pt-3 border-t border-gray-100" data-testid="frontend-base-url-block">
          <label className="block text-[11px] uppercase tracking-widest text-gray-600 mb-1">
            URL base del frontend (FRONTEND_BASE_URL)
          </label>
          <div className="flex items-center gap-2 mb-2 text-xs">
            <span className="text-gray-500">Estado:</span>
            <span className={`px-2 py-0.5 border rounded-sm ${fbuMeta.is_set ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-gray-50 border-gray-300 text-gray-600"}`}>
              {fbuMeta.is_set ? `Configurada: ${fbuMeta.value}` : "Usando default https://procure.justsapiens.com"}
            </span>
            {fbuMeta.source && (
              <span className="text-[10px] uppercase tracking-widest text-gray-500">source: {fbuMeta.source}</span>
            )}
          </div>
          <div className="flex gap-2">
            <Input
              value={fbu}
              onChange={(e) => setFbu(e.target.value)}
              placeholder="https://procure.justsapiens.com"
              className="rounded-sm h-9 text-xs font-mono"
              data-testid="frontend-base-url-input"
            />
            <Button
              onClick={() => save("frontend_base_url", fbu, "fbu")}
              disabled={saving.fbu || !fbu.trim()}
              className="rounded-sm h-9 text-xs"
              data-testid="frontend-base-url-save"
            >
              {saving.fbu ? "Guardando…" : "Guardar"}
            </Button>
          </div>
          <p className="text-[10px] text-gray-500 mt-1">
            Usado como dominio de los enlaces dentro de los emails transaccionales (verificación de cuenta, magic links, etc.).
            <strong> Si no se configura aquí ni vía env, se usa el dominio default de producción.</strong>
          </p>
        </div>
      </div>

      <h2 className="font-serif text-xl text-gray-900 mb-3 mt-8">QR Bre-B (subir imagen)</h2>
      <p className="text-sm text-gray-600 mb-4">
        El QR Bre-B lo genera tu entidad bancaria. Sube la imagen del QR aquí y opcionalmente
        registra la llave Bre-B asociada para mostrársela al usuario.
      </p>

      <div className="border border-gray-200 bg-white p-5 space-y-5">
        {/* Imagen del QR Bre-B */}
        <div>
          <label className="block text-[11px] uppercase tracking-widest text-gray-600 mb-1">
            Imagen QR Bre-B
          </label>
          <div className="flex items-center gap-2 mb-2 text-xs">
            <span className="text-gray-500">Estado:</span>
            <span className={`px-2 py-0.5 border rounded-sm ${ed.manual_payment_qr_image_base64?.is_set ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-gray-50 border-gray-300 text-gray-600"}`}>
              {ed.manual_payment_qr_image_base64?.is_set ? "Imagen cargada" : "Sin imagen"}
            </span>
          </div>
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              if (file.size > 1_500_000) {
                toast.error("Imagen demasiado grande (máx 1.5 MB)");
                e.target.value = "";
                return;
              }
              const reader = new FileReader();
              reader.onload = async () => {
                const b64 = reader.result;  // data:image/...;base64,...
                if (typeof b64 !== "string") return;
                setSaving((s) => ({ ...s, qrImg: true }));
                try {
                  await api.post("/admin/settings", { key: "manual_payment_qr_image_base64", value: b64 });
                  toast.success("Imagen QR Bre-B guardada");
                  reload();
                } catch (err) {
                  toast.error(formatApiError(err?.response?.data?.detail) || "Error subiendo imagen");
                } finally {
                  setSaving((s) => ({ ...s, qrImg: false }));
                  e.target.value = "";
                }
              };
              reader.readAsDataURL(file);
            }}
            className="block text-xs"
            data-testid="bre-b-qr-upload"
            disabled={saving.qrImg}
          />
          <p className="text-[10px] text-gray-500 mt-1">PNG, JPG o WebP. Máximo 1.5 MB.</p>
          {ed.manual_payment_qr_image_base64?.is_set && (
            <div className="mt-3 inline-block border border-gray-200 bg-white p-3">
              <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">Imagen actual</p>
              <img
                src={ed.manual_payment_qr_image_base64.raw_value || ""}
                alt="QR Bre-B actual"
                className="w-[180px] h-[180px] object-contain"
                data-testid="bre-b-current-qr"
              />
            </div>
          )}
        </div>

        {/* Llave Bre-B (informativo) */}
        <div className="pt-4 border-t border-gray-100">
          <label className="block text-[11px] uppercase tracking-widest text-gray-600 mb-1">
            Llave Bre-B (opcional, informativo)
          </label>
          <div className="flex items-center gap-2 mb-2 text-xs">
            <span className="text-gray-500">Estado:</span>
            <span className={`px-2 py-0.5 border rounded-sm ${breBMeta.is_set ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-gray-50 border-gray-300 text-gray-600"}`}>
              {breBMeta.is_set ? `Configurada: ${breBMeta.value || breBMeta.raw_value}` : "Sin configurar"}
            </span>
          </div>
          <div className="flex gap-2">
            <Input
              value={breBKey}
              onChange={(e) => setBreBKey(e.target.value)}
              placeholder="@3001234567 o llave Bre-B oficial"
              className="rounded-sm h-9 text-xs"
              data-testid="bre-b-key-input"
            />
            <Button
              onClick={() => save("manual_payment_bre_b_key", breBKey, "breB")}
              disabled={saving.breB || !breBKey.trim()}
              className="rounded-sm h-9 text-xs"
              data-testid="bre-b-key-save"
            >
              {saving.breB ? "Guardando…" : "Guardar"}
            </Button>
          </div>
          <p className="text-[10px] text-gray-500 mt-1">Se muestra al usuario como dato adicional al QR.</p>
        </div>
      </div>
    </section>
  );
}



// Iter18 — Settings de Operaciones: cleanup (backups_dir, protected_emails) + Quick Reply templates
function OperationsSettings({ settings, reload }) {
  const ed = settings.editable || {};
  const [backupsDir, setBackupsDir] = useState("");
  const [protectedEmailsText, setProtectedEmailsText] = useState("");
  const [quickReplyText, setQuickReplyText] = useState("");
  const [allTemplates, setAllTemplates] = useState([]);
  const [savingBackups, setSavingBackups] = useState(false);
  const [savingProtected, setSavingProtected] = useState(false);
  const [savingQR, setSavingQR] = useState(false);

  useEffect(() => {
    setBackupsDir(ed.backups_dir?.raw_value || ed.backups_dir?.value || "");
    const pe = ed.protected_emails?.raw_value;
    if (Array.isArray(pe)) setProtectedEmailsText(pe.join(", "));
    else setProtectedEmailsText(ed.protected_emails?.value || "");
    const qr = ed.quick_reply_template_ids?.raw_value;
    if (Array.isArray(qr)) setQuickReplyText(qr.join(", "));
    else setQuickReplyText(ed.quick_reply_template_ids?.value || "");
  }, [settings]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/emails/templates?is_active=true");
        setAllTemplates(data.items || []);
      } catch { /* silent */ }
    })();
  }, []);

  const saveBackupsDir = async () => {
    if (!backupsDir || backupsDir.length < 3) return toast.error("Ruta inválida");
    setSavingBackups(true);
    try {
      await api.post("/admin/settings", { key: "backups_dir", value: backupsDir });
      toast.success("Ruta de backups actualizada");
      await reload();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Error guardando"); }
    finally { setSavingBackups(false); }
  };

  const saveProtected = async () => {
    const list = protectedEmailsText.split(",").map((s) => s.trim()).filter(Boolean);
    if (list.length === 0) return toast.error("Debe haber al menos un email protegido");
    setSavingProtected(true);
    try {
      await api.post("/admin/settings", { key: "protected_emails", value: JSON.stringify(list) });
      toast.success(`${list.length} email(s) protegido(s) actualizados`);
      await reload();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Error guardando"); }
    finally { setSavingProtected(false); }
  };

  const saveQuickReplies = async () => {
    const list = quickReplyText.split(",").map((s) => s.trim()).filter(Boolean);
    setSavingQR(true);
    try {
      await api.post("/admin/settings", { key: "quick_reply_template_ids", value: JSON.stringify(list) });
      toast.success(`${list.length} respuesta(s) rápida(s) configurada(s)`);
      await reload();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Error guardando"); }
    finally { setSavingQR(false); }
  };

  const toggleQuickReply = (slug) => {
    const list = quickReplyText.split(",").map((s) => s.trim()).filter(Boolean);
    if (list.includes(slug)) {
      setQuickReplyText(list.filter((s) => s !== slug).join(", "));
    } else {
      setQuickReplyText([...list, slug].join(", "));
    }
  };

  return (
    <section className="mb-10" data-testid="settings-operations">
      <h2 className="font-serif text-xl text-gray-900 mb-3">Operaciones (cleanup + respuestas rápidas)</h2>

      {/* Backups dir */}
      <div className="bg-white border border-gray-200 p-5 mb-4">
        <label className="block text-xs uppercase tracking-widest text-gray-500 mb-1">BACKUPS_DIR</label>
        <p className="text-[11px] text-gray-500 mb-2">
          Directorio donde se guardan los backups antes de cada limpieza de datos de prueba.
          Prioridad: <code>system_config.backups_dir</code> → env <code>BACKUPS_DIR</code> → default <code>/app/backups</code>.
        </p>
        <div className="flex gap-2 items-center">
          <Input
            value={backupsDir}
            onChange={(e) => setBackupsDir(e.target.value)}
            placeholder="/app/backups"
            className="rounded-sm h-9 max-w-md"
            data-testid="settings-backups-dir-input"
          />
          <Button
            className="rounded-sm h-9"
            disabled={savingBackups}
            onClick={saveBackupsDir}
            data-testid="settings-backups-dir-save"
          >
            {savingBackups ? "Guardando…" : "Guardar"}
          </Button>
        </div>
        <p className="text-[11px] text-gray-400 mt-2">
          Origen actual: {ed.backups_dir?.source || "default"}
          {ed.backups_dir?.updated_at && ` · actualizado ${formatDate(ed.backups_dir.updated_at)}`}
        </p>
      </div>

      {/* Protected emails */}
      <div className="bg-white border border-gray-200 p-5 mb-4">
        <label className="block text-xs uppercase tracking-widest text-gray-500 mb-1">PROTECTED_EMAILS</label>
        <p className="text-[11px] text-gray-500 mb-2">
          Emails que <strong>NUNCA</strong> serán borrados al ejecutar la limpieza de datos de prueba.
          Separa con comas. Default: 4 cuentas internas (admin/revisor/usuario.demo/hender.puello).
        </p>
        <Textarea
          value={protectedEmailsText}
          onChange={(e) => setProtectedEmailsText(e.target.value)}
          rows={3}
          placeholder="admin@procure.justsapiens.com, revisor@procure.justsapiens.com"
          className="rounded-sm font-mono text-xs"
          data-testid="settings-protected-emails-input"
        />
        <div className="flex justify-end mt-2">
          <Button
            className="rounded-sm h-9"
            disabled={savingProtected}
            onClick={saveProtected}
            data-testid="settings-protected-emails-save"
          >
            {savingProtected ? "Guardando…" : "Guardar"}
          </Button>
        </div>
        <p className="text-[11px] text-gray-400 mt-2">
          Origen actual: {ed.protected_emails?.source || "default"}
          {ed.protected_emails?.updated_at && ` · actualizado ${formatDate(ed.protected_emails.updated_at)}`}
        </p>
      </div>

      {/* Quick Reply Templates */}
      <div className="bg-white border border-gray-200 p-5">
        <label className="block text-xs uppercase tracking-widest text-gray-500 mb-1">QUICK_REPLY_TEMPLATE_IDS</label>
        <p className="text-[11px] text-gray-500 mb-3">
          Slugs o IDs de plantillas que aparecerán como <strong>botones de respuesta rápida</strong> sobre el editor del Composer.
          Click sobre una plantilla en la lista de abajo para añadir/quitar. El orden de los chips determina el orden en el Composer.
        </p>

        {allTemplates.length === 0 ? (
          <p className="text-xs text-gray-400">No hay plantillas disponibles.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5 mb-3" data-testid="settings-quick-replies-picker">
            {allTemplates.map((t) => {
              const slug = t.slug || t.id;
              const selected = quickReplyText.split(",").map((s) => s.trim()).includes(slug);
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => toggleQuickReply(slug)}
                  className={`text-[11px] px-2.5 py-1 rounded-sm border transition-colors ${
                    selected
                      ? "bg-brand-burgundy text-white border-brand-burgundy"
                      : "bg-white text-gray-700 border-gray-300 hover:border-brand-burgundy hover:text-brand-burgundy"
                  }`}
                  data-testid={`settings-qr-toggle-${slug}`}
                >
                  {selected ? "✓ " : ""}{t.name}
                </button>
              );
            })}
          </div>
        )}

        <Textarea
          value={quickReplyText}
          onChange={(e) => setQuickReplyText(e.target.value)}
          rows={2}
          placeholder="welcome, payment_pending, diagnosis_ready"
          className="rounded-sm font-mono text-xs"
          data-testid="settings-quick-replies-input"
        />
        <div className="flex justify-end mt-2">
          <Button
            className="rounded-sm h-9"
            disabled={savingQR}
            onClick={saveQuickReplies}
            data-testid="settings-quick-replies-save"
          >
            {savingQR ? "Guardando…" : "Guardar"}
          </Button>
        </div>
        <p className="text-[11px] text-gray-400 mt-2">
          Origen actual: {ed.quick_reply_template_ids?.source || "default"}
          {ed.quick_reply_template_ids?.updated_at && ` · actualizado ${formatDate(ed.quick_reply_template_ids.updated_at)}`}
        </p>
      </div>
    </section>
  );
}
